import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import apiClient from '@/api-client';
import { logger } from '@/lib/default-logger';
import { toast } from '@/components/core/toaster';
import { accountUsersKeys } from './use-account-users';

export interface User {
  id: string;
  name: string;
  email: string;
  isSuperAdmin: boolean;
}

export interface UsersResponse {
  data: User[];
  total: number;
}

export interface UserFormData {
  name: string;
  email: string;
  password?: string;
  isSuperAdmin: boolean;
}

export const usersKeys = {
  all: ['users'] as const,
  lists: () => [...usersKeys.all, 'list'] as const,
  list: (query?: string) => [...usersKeys.lists(), { query }] as const,
  details: () => [...usersKeys.all, 'detail'] as const,
  detail: (id: string) => [...usersKeys.details(), id] as const,
};

export function useUsersList(query?: string) {
  return useQuery({
    queryKey: usersKeys.list(query),
    queryFn: async (): Promise<UsersResponse> => {
      const queryString = query ? `?${query}` : '';
      const response = await apiClient.get(`/users${queryString}`);
      return response.data;
    },
  });
}

export function useUser(id: string) {
  return useQuery({
    queryKey: usersKeys.detail(id),
    queryFn: async (): Promise<User> => {
      const response = await apiClient.get(`/users/${id}`);
      return response.data;
    },
    enabled: !!id,
  });
}

export function useCreateUser() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: UserFormData): Promise<User> => {
      const response = await apiClient.post('/users', {
        email: data.email,
        name: data.name,
        password: data.password,
        isSuperAdmin: data.isSuperAdmin,
      });
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: usersKeys.lists() });
      queryClient.invalidateQueries({ queryKey: accountUsersKeys.lists() });
      toast.success('User created successfully');
    },
    onError: (error: any) => {
      logger.error(error);
      const errorMessage = error?.response?.data?.message;
      toast.error(Array.isArray(errorMessage) ? errorMessage[0] : errorMessage || 'Something went wrong!');
    },
  });
}

export function useUpdateUser() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, data }: { id: string; data: UserFormData }): Promise<User> => {
      const response = await apiClient.put(`/users/${id}`, {
        email: data.email,
        name: data.name,
        ...(data.password && { password: data.password }),
        isSuperAdmin: data.isSuperAdmin,
      });
      return response.data;
    },
    onSuccess: (data, variables) => {
      queryClient.setQueryData(usersKeys.detail(variables.id), data);
      queryClient.invalidateQueries({ queryKey: accountUsersKeys.lists() });
      queryClient.invalidateQueries({ queryKey: usersKeys.lists() });
      toast.success('User updated successfully');
    },
    onError: (error: any) => {
      logger.error(error);
      const errorMessage = error?.response?.data?.message;
      toast.error(Array.isArray(errorMessage) ? errorMessage[0] : errorMessage || 'Something went wrong!');
    },
  });
}

export function useDeleteUser() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: string): Promise<void> => {
      await apiClient.delete(`/users/${id}`);
    },
    onSuccess: (_, id) => {
      queryClient.invalidateQueries({ queryKey: accountUsersKeys.lists() });
      queryClient.removeQueries({ queryKey: usersKeys.detail(id) });
      queryClient.invalidateQueries({ queryKey: usersKeys.lists() });
      toast.success('User deleted successfully');
    },
    onError: (error: any) => {
      logger.error(error);
      toast.error('Something went wrong!');
    },
  });
}

export function useUpdateUserProfile() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: { name: string; password?: string, confirmPassword?: string }): Promise<User> => {
      const response = await apiClient.put('/users/me', data, {
        withCredentials: true,
      });
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: usersKeys.lists() });
      queryClient.invalidateQueries({ queryKey: accountUsersKeys.lists() });
      toast.success('Profile updated successfully');
    },
    onError: (error: any) => {
      logger.error(error);
      const errorMessage = error?.response?.data?.message;
      toast.error(Array.isArray(errorMessage) ? errorMessage[0] : errorMessage || 'Something went wrong!');
    },
  });
}